/**
 * BatchRecorderPage - 모든 시나리오 자동 캡처
 * cardAction1~5까지 24fps로 자동 캡처
 */

import React, { useState, useEffect } from "react";
import { ScenarioMotionRecorder } from "../components/motion/ScenarioMotionRecorder";
import {
  cardAction1Scenario,
  cardAction2Scenario,
  cardAction3Scenario,
  cardAction4Scenario,
  cardAction5Scenario,
} from "../components/motion/scenarios";

const BatchRecorderPage: React.FC = () => {
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [completedScenarios, setCompletedScenarios] = useState<string[]>([]);

  const scenarios = [
    {
      id: "cardAction1",
      scenario: cardAction1Scenario,
      title: "빠른 환자 등록",
      description: "간편한 환자 정보 입력",
    },
    {
      id: "cardAction2",
      scenario: cardAction2Scenario,
      title: "실시간 차트 업데이트",
      description: "즉시 반영되는 진료 내용",
    },
    {
      id: "cardAction3",
      scenario: cardAction3Scenario,
      title: "스마트 검색",
      description: "빠르고 정확한 환자 찾기",
    },
    {
      id: "cardAction4",
      scenario: cardAction4Scenario,
      title: "자동 백업",
      description: "안전한 데이터 보호",
    },
    {
      id: "cardAction5",
      scenario: cardAction5Scenario,
      title: "맞춤형 리포트",
      description: "한눈에 보는 통계",
    },
  ];

  const currentScenario = scenarios[currentScenarioIndex];

  // 배치 캡처 시작
  const startBatchCapture = () => {
    setIsRunning(true);
    setCurrentScenarioIndex(0);
    setCompletedScenarios([]);
  };

  // 다음 시나리오로 이동
  const handleScenarioComplete = () => {
    const scenarioId = scenarios[currentScenarioIndex].id;
    setCompletedScenarios((prev) => [...prev, scenarioId]);

    if (currentScenarioIndex < scenarios.length - 1) {
      // 다음 시나리오로
      setTimeout(() => {
        setCurrentScenarioIndex((prev) => prev + 1);
      }, 1000);
    } else {
      // 모든 시나리오 완료
      setIsRunning(false);
      alert("모든 시나리오 캡처가 완료되었습니다!");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: "40px 20px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            marginBottom: "10px",
            color: "#333",
          }}
        >
          🎬 Batch Recorder - CardAction 1~5
        </h1>
        <p style={{ fontSize: "16px", color: "#666", marginBottom: "30px" }}>
          모든 CardAction 시나리오를 24fps로 자동 캡처합니다.
        </p>

        {/* 진행 상황 */}
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "30px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h3 style={{ marginBottom: "15px", fontSize: "18px" }}>
            진행 상황: {completedScenarios.length} / {scenarios.length}
          </h3>

          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            {scenarios.map((scenario, index) => (
              <div
                key={scenario.id}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: completedScenarios.includes(scenario.id)
                    ? "#4CAF50"
                    : index === currentScenarioIndex && isRunning
                    ? "#2196F3"
                    : "#e0e0e0",
                  color:
                    completedScenarios.includes(scenario.id) ||
                    (index === currentScenarioIndex && isRunning)
                      ? "white"
                      : "#666",
                }}
              >
                {scenario.id}
                {completedScenarios.includes(scenario.id) && " ✓"}
                {index === currentScenarioIndex && isRunning && " ⏳"}
              </div>
            ))}
          </div>

          {!isRunning ? (
            <button
              onClick={startBatchCapture}
              style={{
                padding: "12px 24px",
                fontSize: "16px",
                fontWeight: "600",
                backgroundColor: "#667eea",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
              }}
            >
              배치 캡처 시작
            </button>
          ) : (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                color: "#1976d2",
                fontWeight: "600",
              }}
            >
              캡처 진행 중... ({currentScenarioIndex + 1}/{scenarios.length})
            </div>
          )}
        </div>

        {/* Recorder */}
        {isRunning && currentScenario && (
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <ScenarioMotionRecorder
              key={currentScenario.id}
              scenario={currentScenario.scenario}
              title={currentScenario.title}
              description={currentScenario.description}
              frameRate={20}
              width={800}
              height={600}
              autoStart={true}
              onComplete={handleScenarioComplete}
              filePrefix={currentScenario.id}
            />
          </div>
        )}

        {/* 안내 */}
        <div
          style={{
            marginTop: "30px",
            padding: "20px",
            backgroundColor: "#e3f2fd",
            borderRadius: "8px",
            borderLeft: "4px solid #2196f3",
          }}
        >
          <h4 style={{ marginBottom: "10px", color: "#1976d2" }}>
            💡 자동 배치 캡처
          </h4>
          <ul style={{ paddingLeft: "20px", color: "#555", lineHeight: "1.8" }}>
            <li>프레임레이트: 24fps (영화 표준)</li>
            <li>캡처 크기: 800x600px</li>
            <li>각 시나리오가 자동으로 순차 실행되며 프레임이 저장됩니다</li>
            <li>
              파일명: cardAction1_frame_0000.png ~ cardAction5_frame_XXXX.png
            </li>
            <li>모든 캡처 완료 시 알림이 표시됩니다</li>
          </ul>
        </div>

        {/* FFmpeg 일괄 변환 명령어 */}
        <div
          style={{
            marginTop: "20px",
            padding: "20px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          <h4 style={{ marginBottom: "10px" }}>🎥 일괄 GIF 변환 스크립트</h4>
          <code
            style={{
              display: "block",
              padding: "15px",
              backgroundColor: "#263238",
              color: "#aed581",
              borderRadius: "4px",
              fontSize: "13px",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {`# Windows PowerShell
for ($i=1; $i -le 5; $i++) {
  ffmpeg -framerate 24 -i "cardAction$($i)_frame_%04d.png" -vf "fps=24,scale=800:-1:flags=lanczos,split[s0][s1];[s0]palettegen[p];[s1][p]paletteuse" "cardAction$($i).gif"
}

# Linux/Mac Bash
for i in {1..5}; do
  ffmpeg -framerate 24 -i "cardAction\${i}_frame_%04d.png" -vf "fps=24,scale=800:-1:flags=lanczos,split[s0][s1];[s0]palettegen[p];[s1][p]paletteuse" "cardAction\${i}.gif"
done`}
          </code>
        </div>
      </div>
    </div>
  );
};

export default BatchRecorderPage;
